// Copyright 2020, J. Donald Tillman.  All rights reserved.
// Waveform Workbench app.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FunctionInput } from './function-input';
import { Slider } from './slider';
import { Scope } from './scope';
import { updateWave, initAudio, setFreq, setGain } from './engine';

const defaultFunction = 'sin(2 * PI * (x + a))';

// convert pitch to frequency, zero is the low A on the piano, middle C is 39
const pitchFreq = pitch => Math.pow(2, pitch / 12) * 440 / 16;

const chromaticScale = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];

const prettyPitch = (p) => `${chromaticScale[(p + 9) % 12]}, ${pitchFreq(p).toFixed(1)}Hz`;


function App() {
    const [userFunction, setUserFunction] = useState(defaultFunction);
    const [parameter, setParameter] = useState(0);
    const [pitch, setPitch] = useState(27);
    const [volume, setVolume] = useState(50);
    const [audioOn, setAudioOn] = useState(false);

    const scopeRef = useRef();
    const ticksN = 400;
    const waveformN = 256;
    const waveform = new Float32Array(waveformN);
    const getParameterVal = () => parameter / ticksN;

    useEffect(() => {
        parser(userFunction);
        updateWave(waveform, scopeRef.current);
    },
              // eslint-disable-next-line
              [userFunction, parameter]);

    useEffect(() => setFreq(pitchFreq(pitch)),
              [pitch]);

    useEffect(() => setGain(audioOn ? (volume / 100) : 0),
              [volume, audioOn]);

    const handleAudioStart = () => {
        initAudio();
        setFreq(pitchFreq(pitch));
        setGain(volume / 100);
    };

    // parses JavaScript function text
    // runs it for the values
    // returns an error message, or ''
    // leaves a wave in the waveform array
    const parser = str => {
        try {
	    // eslint-disable-next-line
	    const func = Function('x',
				  'a',
				  ...Object.getOwnPropertyNames(Math).map(n => `${n}=Math.${n}`),
				  `{return ${str}};`);
            for (let i = 0; i < waveformN; i++) {
                waveform[i] = func(i / waveformN, getParameterVal());
            }
            return '';
        } catch (error) {
            return error.message;
        }
    };

    // triangle wave function installed on window for Function accessibility
    window.tri = (x) => 4 * Math.max(Math.min(x % 1, .5 - (x % 1)), (x % 1) - 1);

    // web page presets installed on window
    window.setWave = (a) => {
        // weird little hack to convert entities to characters
        const textarea = document.createElement('textarea');
        textarea.innerHTML = a.innerHTML;
        setUserFunction(textarea.value.trim());
    };

    return (
        <Frame>
          <Top>
            <Title>Waveform Workbench</Title> 
          </Top>
          <Main>
            <StartButton onFirst={handleAudioStart}
                         onChange={setAudioOn} />
            <FunctionInput value={userFunction}
	                   parser={parser}
                           onSet={setUserFunction}/>
            <p>
              Waveform and spectrum:
            </p>
            <Scope waveformSize={256}
                   cycleCount={2}
                   ref={scopeRef} />
            <div>
              <Slider label="Parameter:"
                      value={parameter}
                      range={400}
                      onChange={setParameter}
                      readout={getParameterVal()}
              />
              <Slider label="Pitch:"
                      value={pitch}
                      range={88}
                      onChange={setPitch}
                      readout={prettyPitch(pitch)} />

              <Slider label="Volume:"
                      value={volume}
                      range={100}
                      onChange={setVolume}
                      readout={volume}
              />
            </div>
          </Main>
        </Frame>
    );
}

const Frame = styled.div`
    width: 750px;
    margin: 0;
    border: solid 1px gray;
    font-family: Helvetica, sans-serif;
    font-size: 90%;
    display: flex;
    flex-direction: column;
`;

const Title = styled.div`
    font-size: 150%;
    text-align: center;
`;

const Top = styled.div`
    border-bottom: solid 1px gray;
    padding: 10px 0 12px 0;
`;

const Main = styled.div`
    padding: 0 18px;
    margin: 18px;
`;

export default App;


// On/off button that also tells us the first time it's been hit.
const StartButton = props => {
    const { onFirst, onChange } = props;
    const [ state, setState ] = useState(null);

    const handleClick = (e) => {
        (null === state) && onFirst && onFirst();
        onChange && onChange(!state);
        setState(!state);
    };

    return (
        <FloatRight>
        <button onClick={handleClick}>{state ? 'Stop' : 'Start'} Audio</button>
        </FloatRight>
    );
};

const FloatRight = styled.div`
    float: right;
`;


